import React, { useMemo, useState } from 'react';
import T from './theme';

const KEYS = ['kcal', 'cho_g', 'pro_g', 'fat_g', 'na_mg', 'fiber_g'];

export const BADGE_LABEL = {
  usda: 'USDA',
  house: 'house',
  mine: 'mine',
  estimate: '⚠ estimate',
  unmatched: '⚠ not found',
  cycle: '⚠ loop',
};

// How the counted grams were arrived at. The athlete's words stay the label;
// this says what the number behind them rests on.
export const BASIS_LABEL = {
  mass: 'weighed',
  count: 'count',
  volume: 'measured',
  estimate: '⚠ gram estimate',
  servings: 'servings',
};

function round1(value) {
  return Math.round((Number(value) || 0) * 10) / 10;
}

function amountText(qty, unit) {
  const q = round1(qty);
  return unit ? `${q} ${unit}` : String(q);
}

// Grams for ONE of the stated unit, so an edited amount re-scales the grams
// the same way it re-scales the macros.
function gramsPerUnit(row) {
  const grams = Number(row?.grams);
  if (!Number.isFinite(grams) || grams <= 0) return null;
  const qty = Number(row?.qty);
  return grams / (Number.isFinite(qty) && qty > 0 ? qty : 1);
}

function gramsEstPerUnit(row) {
  const est = Number(row?.grams_est);
  if (!Number.isFinite(est) || est <= 0) return null;
  const qty = Number(row?.qty);
  return est / (Number.isFinite(qty) && qty > 0 ? qty : 1);
}

export function rowGrams(row) {
  if (row?.gramsPer == null) return null;
  const qty = Number(row.qtyText);
  return row.gramsPer * (Number.isFinite(qty) && qty > 0 ? qty : 0);
}

function gramsText(row) {
  const grams = rowGrams(row);
  return grams == null ? '' : `${round1(grams)} g`;
}

// Per one labeled portion, so an edited quantity scales the way the backend
// scales a catalog row. An estimate row already carries its per-portion macros.
function perPortion(row) {
  if (row && row.macros_per_qty) {
    return KEYS.reduce((acc, k) => ({ ...acc, [k]: Number(row.macros_per_qty[k]) || 0 }), {});
  }
  const qty = Number(row?.qty);
  const div = Number.isFinite(qty) && qty !== 0 ? qty : 1;
  return KEYS.reduce((acc, k) => ({ ...acc, [k]: (Number(row?.[k]) || 0) / div }), {});
}

export function toRows(ingredients) {
  return (ingredients || []).map((row, i) => ({
    key: `${row.name || 'row'}-${i}`,
    name: row.name || '',
    qtyText: String(row.qty != null ? row.qty : 1),
    unit: row.unit || null,
    amount: row.amount || '',
    portion_label: row.portion_label || '',
    badge: row.badge || 'unmatched',
    estimated: Boolean(row.estimated),
    macros_per_qty: row.macros_per_qty || null,
    gramsEstPer: gramsEstPerUnit(row),
    basis: row.portion_basis || null,
    portionFlag: row.portion_flag || null,
    gramsPer: gramsPerUnit(row),
    per: perPortion(row),
  }));
}

export function rowInvalid(row) {
  if (!row || !String(row.name || '').trim()) return true;
  const qty = Number(row.qtyText);
  return !Number.isFinite(qty) || qty <= 0;
}

export function subtotal(row) {
  const qty = Number(row.qtyText);
  const factor = Number.isFinite(qty) && qty > 0 ? qty : 0;
  return KEYS.reduce((acc, k) => ({ ...acc, [k]: (row.per[k] || 0) * factor }), {});
}

export function cardTotals(rows) {
  return (rows || []).reduce(
    (acc, row) => {
      const sub = subtotal(row);
      KEYS.forEach((k) => {
        acc[k] += sub[k] || 0;
      });
      return acc;
    },
    KEYS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {})
  );
}

function payloadFor(rows) {
  return rows.map((row) => ({
    name: String(row.name || '').trim(),
    qty: Number(row.qtyText) || 1,
    unit: row.unit || null,
    amount: row.unit ? amountText(row.qtyText, row.unit) : row.amount || amountText(row.qtyText, null),
    // Per one stated unit, so an edited amount re-runs the same conversion on
    // the server instead of inheriting the first answer's grams.
    grams_est:
      row.gramsEstPer == null ? null : round1(row.gramsEstPer * (Number(row.qtyText) || 1)),
    macros_per_qty: row.macros_per_qty || null,
  }));
}

/**
 * Approximation card: what UMAX thinks is in a named dish, before it counts.
 * Nothing here is on the day's totals until Accept.
 */
export default function ApproximationCard({ api, mealId, card, onAccepted, onCancelled }) {
  const [dish, setDish] = useState(card?.dish || '');
  const [servingLabel, setServingLabel] = useState(card?.serving_label || '1 serving');
  const [rows, setRows] = useState(() => toRows(card?.ingredients));
  const [newName, setNewName] = useState('');
  const [newQty, setNewQty] = useState('1');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const totals = useMemo(() => cardTotals(rows), [rows]);
  const anyInvalid = rows.some(rowInvalid);
  const canAccept = rows.length > 0 && !anyInvalid && !busy && Boolean(String(dish).trim());

  const setQty = (index, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, qtyText: value } : row)));
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const applyCard = (next) => {
    if (!next) return;
    if (next.dish) setDish(next.dish);
    if (next.serving_label) setServingLabel(next.serving_label);
    setRows(toRows(next.ingredients));
  };

  // A new ingredient goes through the same resolution chain on the server.
  const addRow = async () => {
    const name = newName.trim();
    if (!name) {
      setError('Name the ingredient.');
      return;
    }
    const qty = Number(newQty);
    const next = [
      ...payloadFor(rows),
      {
        name,
        qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
        unit: null,
        amount: null,
        grams_est: null,
        macros_per_qty: null,
      },
    ];
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/nutrition/approximate/${mealId}/preview`, {
        dish,
        serving_label: servingLabel,
        ingredients: next,
      });
      applyCard(res.data?.card);
      setNewName('');
      setNewQty('1');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not resolve that ingredient');
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/nutrition/approximate/${mealId}/accept`, {
        dish,
        serving_label: servingLabel,
        ingredients: payloadFor(rows),
        save_recipe: true,
      });
      if (onAccepted) onAccepted(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Accept failed');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post(`/nutrition/approximate/${mealId}/cancel`, {});
      if (onCancelled) onCancelled(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        border: `1px solid ${T.color.border}`,
        borderLeft: `3px solid ${T.color.accentNutrition}`,
        borderRadius: T.radius.base,
        padding: 12,
        margin: '8px 0',
      }}
    >
      <h4 style={{ margin: '0 0 4px' }}>Approximation · awaiting your approval</h4>
      <p style={{ fontSize: T.font.sm, color: T.color.textMuted, margin: '0 0 8px' }}>
        {card?.source === 'recipe' ? 'From your saved recipe.' : 'Ingredients proposed, macros computed here.'}
        {' '}Nothing counts toward today until you accept.
      </p>
      <label style={{ display: 'block', fontSize: T.font.sm, marginBottom: 6 }}>
        Dish
        <input
          value={dish}
          onChange={(e) => setDish(e.target.value)}
          aria-label="dish name"
          style={{ display: 'block', width: '100%', boxSizing: 'border-box' }}
        />
      </label>
      <label style={{ display: 'block', fontSize: T.font.sm, marginBottom: 8 }}>
        Serving
        <input
          value={servingLabel}
          onChange={(e) => setServingLabel(e.target.value)}
          aria-label="serving label"
          style={{ display: 'block', width: 160 }}
        />
      </label>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.font.sm }}>
        <thead>
          <tr>
            <th style={T.cellHeaderStyle}>Ingredient</th>
            <th style={T.cellHeaderStyle}>Amount</th>
            <th style={T.cellHeaderStyle}>Source</th>
            <th style={T.cellHeaderStyle}>Subtotal</th>
            <th style={T.cellHeaderStyle} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const sub = subtotal(row);
            const bad = rowInvalid(row);
            return (
              <tr key={row.key}>
                <td style={T.cellStyle}>
                  {row.name}
                  {row.portion_label ? (
                    <span style={{ color: T.color.textLight }}> · {row.portion_label}</span>
                  ) : null}
                </td>
                <td style={T.cellStyle}>
                  <input
                    value={row.qtyText}
                    onChange={(e) => setQty(i, e.target.value)}
                    aria-label={`amount for ${row.name}`}
                    style={{ width: 64, borderColor: bad ? T.color.danger : undefined }}
                  />
                  {row.unit ? <span style={{ color: T.color.textLight }}> {row.unit}</span> : null}
                  {row.amount ? (
                    <span style={{ display: 'block', fontSize: T.font.xs, color: T.color.textLight }}>
                      asked: {row.amount}
                    </span>
                  ) : null}
                  {/* What actually got counted, and on what evidence. */}
                  {row.basis ? (
                    <span
                      data-testid={`grams-${i}`}
                      style={{
                        display: 'block',
                        fontSize: T.font.xs,
                        color: row.basis === 'estimate' ? T.color.warning : T.color.textLight,
                      }}
                    >
                      {gramsText(row) ? `${gramsText(row)} · ` : ''}
                      {BASIS_LABEL[row.basis] || row.basis}
                    </span>
                  ) : null}
                </td>
                <td style={T.cellStyle}>
                  <span
                    style={{
                      border: `1px solid ${T.color.borderLight}`,
                      borderRadius: T.radius.pill,
                      padding: '1px 6px',
                      fontSize: T.font.xs,
                      color: row.estimated ? T.color.warning : T.color.textMuted,
                    }}
                  >
                    {BADGE_LABEL[row.badge] || row.badge}
                  </span>
                </td>
                <td style={T.cellStyle} data-testid={`subtotal-${i}`}>
                  {Math.round(sub.kcal)} kcal · {round1(sub.cho_g)} C
                </td>
                <td style={T.cellStyle}>
                  <button type="button" onClick={() => removeRow(i)} aria-label={`remove ${row.name}`}>
                    ×
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p style={{ fontSize: T.font.base, margin: '8px 0' }} data-testid="dish-total">
        {servingLabel}: {Math.round(totals.kcal)} kcal · {round1(totals.cho_g)} g carbs ·{' '}
        {round1(totals.pro_g)} g protein · {round1(totals.fat_g)} g fat · {Math.round(totals.na_mg)} mg sodium
      </p>
      {anyInvalid && (
        <p style={{ fontSize: T.font.sm, color: T.color.danger, margin: '4px 0' }}>
          Every row needs a name and an amount above zero.
        </p>
      )}
      {rows.some(
        (r) =>
          r.badge === 'estimate' ||
          r.badge === 'unmatched' ||
          r.basis === 'estimate' ||
          r.portionFlag
      ) && (
        <p style={{ fontSize: T.font.sm, color: T.color.warning, margin: '4px 0' }}>
          Flagged rows are model estimates, unmatched, or an amount we could not convert from the
          source — fix them if you can.
        </p>
      )}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '8px 0' }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="add ingredient"
          aria-label="new ingredient name"
          style={{ flex: 1 }}
        />
        <input
          value={newQty}
          onChange={(e) => setNewQty(e.target.value)}
          aria-label="new ingredient amount"
          style={{ width: 64 }}
        />
        <button type="button" onClick={addRow} disabled={busy}>
          Add
        </button>
      </div>

      <p style={{ margin: '8px 0 0' }}>
        <button type="button" onClick={accept} disabled={!canAccept}>
          Accept
        </button>{' '}
        <button type="button" onClick={cancel} disabled={busy}>
          Cancel
        </button>
      </p>
      {card?.disclaimer && (
        <p style={{ fontSize: T.font.xs, color: T.color.textLight, margin: '6px 0 0' }}>{card.disclaimer}</p>
      )}
      {error && <p style={{ color: T.color.danger, fontSize: T.font.sm }}>{String(error)}</p>}
    </div>
  );
}
